import { Alert, Input, Select, Spin } from 'antd';
import { Profile } from 'oidc-client';
import React, { useEffect, useMemo, useState } from 'react';
import { defaultSelectedUserRole, getUserRole } from '../../helpers/CheckPermissionHelper';
import { filterOptionForSearch } from '../../helpers/normalizeForSearch';
import { MatchVm } from '../../utils/api';
import './insights.css';
import { canAccessInsights, canUseInsightsPages } from './insightsAccess';
import { useInsightsClub } from './insightsClubScope';
import { ourTeamIds, summarizeClubMatches } from './insightsClubSummary';
import InsightsDateRangeFilter from './InsightsDateRangeFilter';
import InsightsLegend from './InsightsLegend';
import { matchCountLabel } from './insightsMatchBundleFilters';
import {
    collectMatchTeamOptions,
    emptyInsightsMatchListFilters,
    filterClubMatches,
    InsightsMatchListFilters,
    MATCH_RESULT_VENUE_OPTIONS,
    MatchResultVenueTag,
} from './insightsMatchListFilters';
import InsightsMatchTable from './InsightsMatchTable';
import InsightsMultiSelectDropdown from './InsightsMultiSelectDropdown';
import InsightsNav from './InsightsNav';
import { fetchClubMatches } from './insightsQueries';
import InsightsTopbar from './InsightsTopbar';

interface Props {
    userProfile?: Profile;
}

const PAGE_SIZE = 100;

const InsightsMatches: React.FC<Props> = ({ userProfile }) => {
    // getUserRole returns a new object every call; putting it in effect deps
    // retriggered the page fetch in a tight loop.
    const userRole = useMemo(
        () => (userProfile ? getUserRole(userProfile) : defaultSelectedUserRole),
        [userProfile]
    );
    const allowed = canAccessInsights(userRole);

    const scope = useInsightsClub(userProfile, allowed && canUseInsightsPages(userRole));
    const { selectedClubId, selectedTeamId } = scope;

    const [allMatches, setAllMatches] = useState<MatchVm[]>([]);
    const [filters, setFilters] = useState<InsightsMatchListFilters>(
        emptyInsightsMatchListFilters()
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const teamIds = useMemo(
        () => ourTeamIds(scope.teams, selectedTeamId),
        [scope.teams, selectedTeamId]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            if (!allowed || !canUseInsightsPages(userRole) || selectedClubId == null) {
                setAllMatches([]);
                return;
            }

            setLoading(true);
            setError(undefined);
            try {
                const page = await fetchClubMatches({
                    clubId: selectedClubId,
                    teamId: selectedTeamId,
                    pageSize: PAGE_SIZE,
                });
                if (cancelled) return;
                setAllMatches(page.pageItems || []);
                setFilters(emptyInsightsMatchListFilters());
            } catch {
                if (!cancelled) {
                    setAllMatches([]);
                    setError('Could not load matches.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, selectedClubId, selectedTeamId]);

    const competitionOptions = useMemo(() => {
        const byId = new Map<number, string>();
        allMatches.forEach((m) => {
            if (m.competitionId != null && !byId.has(m.competitionId)) {
                byId.set(
                    m.competitionId,
                    m.competitionDisplayName || `Competition ${m.competitionId}`
                );
            }
        });
        return Array.from(byId.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allMatches]);

    const teamOptions = useMemo(() => collectMatchTeamOptions(allMatches), [allMatches]);

    const filtered = useMemo(
        () => filterClubMatches(allMatches, teamIds, filters),
        [allMatches, teamIds, filters]
    );

    const summary = useMemo(() => summarizeClubMatches(filtered, teamIds), [filtered, teamIds]);

    if (!canUseInsightsPages(userRole)) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Insights is only on staging and the Statistics Portal"
            />
        );
    }

    if (!allowed) {
        return (
            <div className="insightsRoot">
                <InsightsTopbar />
                <div className="insightsBlocked">
                    <h1>Not available for this account</h1>
                    <p>Club Insights is for Federation and Club accounts that enter statistics.</p>
                </div>
            </div>
        );
    }

    const patch = (partial: Partial<InsightsMatchListFilters>): void =>
        setFilters((prev) => ({ ...prev, ...partial }));

    return (
        <div className="insightsRoot">
            <InsightsTopbar scope={scope} />

            <div className="insightsBody">
                <InsightsNav active="matches" />
                <InsightsLegend />

                <div className="insightsHero">
                    <div>
                        <h1>Matches</h1>
                        <p>
                            Browse this club's matches with competition, date, team, and result
                            filters.
                        </p>
                    </div>
                    <div className="insightsStatStrip">
                        <div className="insightsStat">
                            <b>{summary.wins}</b>
                            <i>W</i>
                        </div>
                        <div className="insightsStat">
                            <b>{summary.draws}</b>
                            <i>D</i>
                        </div>
                        <div className="insightsStat">
                            <b>{summary.losses}</b>
                            <i>L</i>
                        </div>
                        <div className="insightsStat">
                            <b>
                                {summary.goalsFor}:{summary.goalsAgainst}
                            </b>
                            <i>GF:GA</i>
                        </div>
                    </div>
                </div>

                <div className="insightsMatchesToolbar">
                    <div className="insightsMatchesFilters">
                        <InsightsDateRangeFilter
                            dateFrom={filters.dateFrom}
                            dateTo={filters.dateTo}
                            onChange={(next) =>
                                patch({ dateFrom: next.dateFrom, dateTo: next.dateTo })
                            }
                        />
                        {competitionOptions.length > 0 ? (
                            <InsightsMultiSelectDropdown
                                label="Competition"
                                options={competitionOptions.map((c) => ({
                                    value: c.id,
                                    label: c.name,
                                }))}
                                selected={filters.competitionIds}
                                onChange={(next) => patch({ competitionIds: next })}
                            />
                        ) : null}
                        <InsightsMultiSelectDropdown
                            label="Result"
                            options={MATCH_RESULT_VENUE_OPTIONS.map((o) => ({
                                value: o.value,
                                label: o.label,
                            }))}
                            selected={filters.resultVenue}
                            onChange={(next: MatchResultVenueTag[] | null) =>
                                patch({ resultVenue: next })
                            }
                        />
                        <Select
                            className="insightsMatchesSelect"
                            allowClear
                            showSearch
                            filterOption={filterOptionForSearch}
                            optionFilterProp="label"
                            placeholder="Team 1 · Home"
                            value={filters.team1Id ?? undefined}
                            options={teamOptions.map((t) => ({
                                value: t.id,
                                label: t.name,
                            }))}
                            onChange={(value: number | undefined) =>
                                patch({ team1Id: value ?? null })
                            }
                        />
                        <Select
                            className="insightsMatchesSelect"
                            allowClear
                            showSearch
                            filterOption={filterOptionForSearch}
                            optionFilterProp="label"
                            placeholder="Team 2 · Away"
                            value={filters.team2Id ?? undefined}
                            options={teamOptions.map((t) => ({
                                value: t.id,
                                label: t.name,
                            }))}
                            onChange={(value: number | undefined) =>
                                patch({ team2Id: value ?? null })
                            }
                        />
                        <Input
                            className="insightsMatchesSearchInput"
                            allowClear
                            placeholder="Search…"
                            value={filters.search}
                            onChange={(e) => patch({ search: e.target.value })}
                        />
                    </div>
                    <div className="insightsScopeMatchCount" aria-live="polite">
                        {matchCountLabel(filtered.length)}
                    </div>
                </div>

                {error && (
                    <Alert type="error" showIcon message={error} style={{ marginBottom: 14 }} />
                )}

                <Spin spinning={loading || scope.loading}>
                    <section className="insightsPanel">
                        <div className="insightsPanelHead">
                            <h2>Match list</h2>
                            <span>
                                {summary.played > 0
                                    ? `${summary.wins}-${summary.draws}-${summary.losses}`
                                    : 'Finished results in the current filter'}
                            </span>
                        </div>
                        <InsightsMatchTable
                            matches={filtered}
                            ourTeamIds={teamIds}
                            emptyText={
                                allMatches.length === 0
                                    ? 'No matches for this club yet.'
                                    : 'No matches for this selection.'
                            }
                        />
                    </section>
                </Spin>
            </div>
        </div>
    );
};

export default InsightsMatches;
